import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowRight, Download, BookOpen, Mail, Sparkles, Heart, Headphones, FileText, Layout } from "lucide-react";

const BRAND = {
  deep: "#042F2E",
  teal: "#068fa2",
  tealLight: "#25a1c2",
  cream: "#efeae7",
  gold: "#d4af6a",
  mint: "#5EEAD4",
};

const RESOURCES = [
  {
    icon: FileText,
    title: "Daily Check-In Poster (Mind · Body · Spirit)",
    desc: "One page. Print it for the fridge or mirror. Run the five before the world comes for you.",
    href: "/printables/Body_CheckIn_Poster.pdf",
    cta: "Download PDF",
    badge: "Free printable",
    testid: "free-checkin-poster",
    external: true,
  },
  {
    icon: FileText,
    title: "Yes. No. Wait. Check-In Cards",
    desc: "A 3-card pocket deck for moments when you cannot hear clearly. Ground. Ask. Listen.",
    href: "/yes-no-wait",
    cta: "Get the cards",
    badge: "Free PDF",
    testid: "free-yes-no-wait",
  },
  {
    icon: BookOpen,
    title: "30-Day Validation Journal",
    desc: "Notice signs, synchronicities & divine validation — daily prompts for the messy middle.",
    href: "/validation-journal",
    cta: "Start the journal",
    badge: "Free PDF",
    testid: "free-validation-journal",
  },
  {
    icon: Sparkles,
    title: "Tapped-In Card Pulls",
    desc: "Interactive daily pulls — no signup needed. Click. Breathe. Receive.",
    href: "/card-pulls",
    cta: "Pull a card",
    badge: "Free interactive",
    testid: "free-card-pulls",
  },
  {
    icon: Layout,
    title: "Acronym Dictionary — Sample Edition",
    desc: "A taste of the 38-code acronym system — frameworks for living by design, not by default.",
    href: "/acronym-dictionary",
    cta: "Read the sample",
    badge: "Free PDF",
    testid: "free-acronym",
  },
  {
    icon: BookOpen,
    title: "The Method — explained",
    desc: "A walk-through of the Mind Body Spirit HYGIENE™ Method. What it is, why it works.",
    href: "/the-method",
    cta: "Read the Method",
    badge: "Free page",
    testid: "free-method",
  },
  {
    icon: FileText,
    title: "The Blog",
    desc: "Long-form articles on boundaries, anxiety, hygiene, and the messy middle.",
    href: "/blog",
    cta: "Browse articles",
    badge: "Free reading",
    testid: "free-blog",
  },
  {
    icon: Headphones,
    title: "Tap-In Audio Essay",
    desc: "A 9-minute essay on the sacred pause — for the moments when you cannot hear yourself.",
    href: "/blog/the-tap-in",
    cta: "Listen now",
    badge: "Free audio",
    testid: "free-tap-in",
  },
  {
    icon: BookOpen,
    title: "Free Book Sample (8 pages)",
    desc: "Want a taste of the book before you buy? Read the first 8 pages, on the house.",
    href: "/book",
    cta: "Read the sample",
    badge: "Free PDF",
    testid: "free-book-sample",
  },
];

export default function FreeResources() {
  return (
    <div className="min-h-screen" style={{ background: BRAND.cream }} data-testid="free-resources-page">
      <Helmet>
        <meta name="keywords" content="free resources, free workbook, free PDF, Mind Body Spirit HYGIENE™, Temple Franklin, I GET TO, lead magnets, free downloads" />
      </Helmet>

      {/* Minimal header */}
      <header className="px-6 py-5 border-b sticky top-0 z-40 backdrop-blur-xl" style={{ background: "rgba(239,234,231,0.85)", borderColor: "rgba(4,47,46,0.08)" }}>
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <Link to="/" className="font-serif text-lg tracking-tight" style={{ color: BRAND.deep }} data-testid="free-back-home">
            Mind Body Spirit HYGIENE<sup className="text-xs">™</sup>
          </Link>
          <nav className="flex gap-4 md:gap-6 text-xs md:text-sm font-sans tracking-wider">
            <Link to="/" className="hover:opacity-70 transition-opacity" style={{ color: BRAND.teal }} data-testid="free-nav-shop">
              Shop
            </Link>
            <Link to="/book" className="hover:opacity-70 transition-opacity" style={{ color: BRAND.teal }} data-testid="free-nav-book">
              The Book
            </Link>
            <Link to="/build-your-own" className="hover:opacity-70 transition-opacity" style={{ color: BRAND.teal }} data-testid="free-nav-build-your-own">
              Build Your Own
            </Link>
            <Link to="/home" className="hover:opacity-70 transition-opacity" style={{ color: BRAND.teal }} data-testid="free-nav-home">
              Temple&rsquo;s Story
            </Link>
          </nav>
        </div>
      </header>

      {/* ─── Page intro ─── */}
      <section className="px-6 pt-12 pb-6 md:pt-20 md:pb-8 text-center">
        <div className="max-w-3xl mx-auto">
          <p
            className="font-sans text-[10px] tracking-[0.35em] uppercase font-bold mb-3"
            style={{ color: BRAND.gold }}
          >
            ✦ Free Resources Library
          </p>
          <h1
            className="font-serif tracking-tighter leading-[1.05] mb-4"
            style={{ fontSize: "clamp(2rem, 4.5vw, 3.5rem)", color: BRAND.deep }}
            data-testid="free-headline"
          >
            Real tools. Real time. <em style={{ color: BRAND.teal }}>Free.</em>
          </h1>
          <p
            className="font-serif text-base md:text-lg leading-relaxed"
            style={{ color: BRAND.teal }}
            data-testid="free-subhead"
          >
            Start with the workbook below — then make yourself at home in the library.
          </p>
        </div>
      </section>

      {/* ─── HERO: The Workbook (the ONE front door) ─── */}
      <section className="px-6 py-10 md:py-14" data-testid="free-workbook-hero">
        <div className="max-w-5xl mx-auto">
          <div
            className="relative overflow-hidden rounded-3xl p-8 md:p-12 grid md:grid-cols-[1fr,260px] gap-8 md:gap-12 items-center"
            style={{
              background: `linear-gradient(135deg, ${BRAND.deep} 0%, ${BRAND.teal} 55%, ${BRAND.tealLight} 100%)`,
            }}
          >
            {/* gold glow */}
            <div
              className="absolute inset-0 pointer-events-none"
              style={{
                background: `radial-gradient(circle at 85% 25%, rgba(212,175,106,0.18) 0%, transparent 55%)`,
              }}
            />
            <div className="relative">
              <p
                className="font-sans text-[10px] tracking-[0.35em] uppercase font-bold mb-3"
                style={{ color: BRAND.gold }}
              >
                Start here ✦ Free 12-page workbook
              </p>
              <h2
                className="font-serif tracking-tighter leading-[1.05] mb-4 text-white"
                style={{ fontSize: "clamp(2rem, 4vw, 3.25rem)" }}
                data-testid="free-workbook-title"
              >
                Your brain is not stuck, Lovely.
              </h2>
              <p
                className="font-serif text-base md:text-lg leading-relaxed mb-6"
                style={{ color: "rgba(255,255,255,0.85)" }}
                data-testid="free-workbook-blurb"
              >
                Download <em>I GET TO</em> — a 12-page practice that teaches you the simple tool that helps you rewire how you talk to yourself. Plus a bonus 365-day affirmation calendar.
              </p>
              <Link
                to="/workbook"
                className="inline-flex items-center justify-center gap-2 rounded-full px-7 py-4 font-sans font-bold tracking-wide transition-all hover:-translate-y-0.5 shadow-xl"
                style={{ background: BRAND.mint, color: BRAND.deep }}
                data-testid="free-workbook-cta"
              >
                <Download className="w-5 h-5" />
                Send me the free workbook
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            {/* mini cover */}
            <div className="relative hidden md:block">
              <div
                className="aspect-[6/8] rounded-md shadow-2xl flex flex-col items-center justify-center text-center px-5"
                style={{
                  background: `linear-gradient(155deg, rgba(4,47,46,0.85) 0%, rgba(6,143,162,0.95) 100%)`,
                  border: "1px solid rgba(255,255,255,0.1)",
                }}
              >
                <p className="font-sans text-[8px] tracking-[0.4em] uppercase font-bold mb-2" style={{ color: BRAND.gold }}>
                  12 pages · free
                </p>
                <h3 className="font-serif text-white tracking-tighter leading-none mb-2 text-4xl">
                  I GET TO
                </h3>
                <p className="font-serif italic text-xs leading-snug" style={{ color: "rgba(255,255,255,0.78)" }}>
                  A practice for rewiring your brain — one true word at a time.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── Library grid ─── */}
      <section className="px-6 pb-16 md:pb-24" data-testid="free-library-grid">
        <div className="max-w-5xl mx-auto">
          <p
            className="font-sans text-[10px] tracking-[0.35em] uppercase font-bold mb-3 text-center"
            style={{ color: BRAND.gold }}
          >
            ✦ More in the library
          </p>
          <h2 className="font-serif text-2xl md:text-3xl tracking-tighter text-center mb-10" style={{ color: BRAND.deep }}>
            Bring whatever you need today.
          </h2>
          <div className="grid sm:grid-cols-2 gap-5">
            {RESOURCES.map((r, i) => {
              const Icon = r.icon;
              // External (PDF/asset) resources use <a download>; internal pages use <Link>
              const isExternal = r.external || r.href.endsWith(".pdf");
              const sharedClass = "group p-6 rounded-2xl bg-white hover:shadow-xl transition-all duration-300 hover:-translate-y-1 border flex flex-col";
              const sharedStyle = { borderColor: "rgba(6,143,162,0.12)" };
              const inner = (
                <>
                  <div className="flex items-start gap-4 mb-3">
                    <div
                      className="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center"
                      style={{ background: BRAND.cream }}
                    >
                      <Icon className="w-5 h-5" style={{ color: BRAND.teal }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <span
                        className="inline-block font-sans text-[9px] tracking-[0.25em] uppercase font-bold px-2 py-1 rounded-full mb-2"
                        style={{ background: BRAND.cream, color: BRAND.teal }}
                      >
                        {r.badge}
                      </span>
                      <h3 className="font-serif text-xl leading-tight" style={{ color: BRAND.deep }}>
                        {r.title}
                      </h3>
                    </div>
                  </div>
                  <p className="font-sans text-sm leading-relaxed mb-4 flex-grow" style={{ color: "rgba(4,47,46,0.7)" }}>
                    {r.desc}
                  </p>
                  <span
                    className="inline-flex items-center gap-1 font-sans text-sm font-medium tracking-wide group-hover:gap-2 transition-all"
                    style={{ color: BRAND.teal }}
                  >
                    {r.cta}
                    <ArrowRight className="w-4 h-4" />
                  </span>
                </>
              );
              return isExternal ? (
                <a
                  href={r.href}
                  download={r.href.split("/").pop()}
                  key={r.testid}
                  className={sharedClass}
                  style={sharedStyle}
                  data-testid={r.testid}
                >
                  {inner}
                </a>
              ) : (
                <Link
                  to={r.href}
                  key={r.testid}
                  className={sharedClass}
                  style={sharedStyle}
                  data-testid={r.testid}
                >
                  {inner}
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* ─── Explore the Shop — internal linking to product pages ─── */}
      <section className="px-6 py-14 md:py-16" style={{ background: BRAND.cream }} data-testid="free-explore-shop">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <p className="font-sans text-[10px] tracking-[0.35em] uppercase font-bold mb-3" style={{ color: BRAND.gold }}>
              ✦ Explore the toolkit
            </p>
            <h2 className="font-serif text-2xl md:text-3xl tracking-tighter mb-3" style={{ color: BRAND.deep }}>
              Tools for the next chapter
            </h2>
            <p className="font-serif italic text-base md:text-lg leading-relaxed max-w-2xl mx-auto" style={{ color: BRAND.teal }}>
              When the free pieces meet you where you are — these are the next steps.
            </p>
          </div>

          <div className="text-center mt-8">
            <Link
              to="/about"
              className="inline-flex items-center gap-1.5 font-sans text-sm font-medium hover:gap-2 transition-all"
              style={{ color: BRAND.teal }}
              data-testid="free-explore-about"
            >
              Meet Temple — the story behind the Method
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* ─── Soft sell: the book ─── */}
      <section className="px-6 py-14 md:py-20" style={{ background: "white" }}>
        <div className="max-w-3xl mx-auto text-center">
          <BookOpen className="w-6 h-6 mx-auto mb-4" style={{ color: BRAND.gold }} />
          <p className="font-sans text-[10px] tracking-[0.35em] uppercase font-bold mb-3" style={{ color: BRAND.gold }}>
            When you&rsquo;re ready
          </p>
          <h2 className="font-serif text-2xl md:text-3xl tracking-tighter mb-4" style={{ color: BRAND.deep }}>
            The book is where the whole practice lives.
          </h2>
          <p className="font-serif italic text-base md:text-lg leading-relaxed mb-6" style={{ color: BRAND.teal }}>
            141 pages. The full A.C.T. NOW Method™. Real tools. Real time. Paperback in your hands.
          </p>
          <Link
            to="/book"
            className="inline-flex items-center justify-center gap-2 rounded-full px-7 py-4 font-sans font-medium tracking-wide transition-colors border"
            style={{ color: BRAND.deep, borderColor: "rgba(4,47,46,0.25)" }}
            data-testid="free-book-cta"
          >
            Read about the book
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      {/* Footer */}
    </div>
  );
}
